import { createNoise2D, createNoise3D } from 'simplex-noise';
import alea from 'alea';
import { CHUNK_SIZE, SEA_LEVEL } from '../constants.js';
import { BlockId } from './BlockType.js';

/**
 * StructureGenerator — Generates underground and overworld structures
 * during terrain generation. All placement is deterministic based on seed.
 *
 * Structures:
 *   1. Dungeon (underground, y < 40)
 *   2. Abandoned Mine (underground, y 15-45)
 *   3. Desert Temple (surface, desert biome)
 *   4. Ruined Portal (surface, any biome)
 *   5. Stronghold (underground, one per world)
 *   6. Jungle Temple (surface, jungle biome)
 *   7. Igloo (surface, tundra biome)
 *   8. Shipwreck (underwater)
 *   9. Witch Hut (surface, swamp biome)
 */
export class StructureGenerator {
  constructor(seed) {
    // Dedicated noise functions for structure placement (deterministic)
    this.structNoise2D = createNoise2D(alea(seed + '-struct2d'));
    this.structNoise3D = createNoise3D(alea(seed + '-struct3d'));
    this.dungeonNoise = createNoise3D(alea(seed + '-dungeon'));
    this.mineNoise = createNoise3D(alea(seed + '-mine'));
    this.templeNoise = createNoise2D(alea(seed + '-temple'));
    this.portalNoise = createNoise2D(alea(seed + '-portal'));
    this.detailNoise = createNoise3D(alea(seed + '-structdetail'));

    this.jungleTempleNoise = createNoise2D(alea(seed + '-jtemple'));
    this.iglooNoise = createNoise2D(alea(seed + '-igloo'));
    this.shipwreckNoise = createNoise2D(alea(seed + '-shipwreck'));
    this.witchHutNoise = createNoise2D(alea(seed + '-witchhut'));

    this.strongholdNoise = createNoise2D(alea(seed + '-stronghold'));

    // Seeded PRNG for loot and random details (per-structure)
    this._seed = seed;

    // Stronghold position (one per world, deterministic)
    this._strongholdPos = null;
    this._computeStrongholdPosition();
  }

  _computeStrongholdPosition() {
    // Deterministic position 300-500 blocks from origin, underground at Y=20
    const angle = (this.strongholdNoise(0.5, 0.5) + 1) * Math.PI; // 0 to 2π
    const dist = 300 + (this.strongholdNoise(1.5, 1.5) + 1) * 100; // 300-500
    this._strongholdPos = {
      x: Math.floor(Math.cos(angle) * dist),
      y: 20,
      z: Math.floor(Math.sin(angle) * dist),
    };
  }

  /** Public getter for Ender Eye targeting */
  getStrongholdPosition() {
    return this._strongholdPos;
  }

  /**
   * Generate all structures that may overlap with the given chunk.
   * Called from TerrainGenerator._generateStructures().
   *
   * @param {object} chunk - The chunk object with setBlock/getBlock
   * @param {function} getHeight - Function(wx, wz) => surface height
   * @param {function} getBiome - Function(wx, wz) => biome id
   * @param {number} BIOME_DESERT - The desert biome constant
   */
  generate(chunk, getHeight, getBiome, BIOME_DESERT, BIOME_JUNGLE, BIOME_TUNDRA, BIOME_SWAMP) {
    this._generateDungeons(chunk);
    this._generateAbandonedMines(chunk);
    this._generateDesertTemples(chunk, getHeight, getBiome, BIOME_DESERT);
    this._generateRuinedPortals(chunk, getHeight, getBiome);
    this._generateStronghold(chunk);
    if (BIOME_JUNGLE !== undefined) {
      this._generateJungleTemples(chunk, getHeight, getBiome, BIOME_JUNGLE);
      this._generateIgloos(chunk, getHeight, getBiome, BIOME_TUNDRA);
      this._generateShipwrecks(chunk, getHeight);
      this._generateWitchHuts(chunk, getHeight, getBiome, BIOME_SWAMP);
    }
  }

  // =========================================================================
  // 1. DUNGEON — Underground room (y < 40)
  //    7x7x5 room, moss_stone walls, cobblestone floor, chest, torches
  //    Probability: ~1 per 4 chunks underground
  // =========================================================================

  _generateDungeons(chunk) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    // Check nearby chunk-grid positions that could place a dungeon overlapping this chunk.
    // Dungeons are anchored per (cx, cz) grid cell — we check neighbors too for cross-chunk.
    const searchRadius = 1;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        // ~25% chance per horizontal chunk position (1 per 4 chunks)
        const chance = this.dungeonNoise(acx * 0.31, 0.5, acz * 0.31);
        if (chance < 0.75) continue;

        // Dungeon center position (world coords), deterministic from chunk coords
        const dungeonX = acx * CHUNK_SIZE + 4 + Math.floor(Math.abs(this.dungeonNoise(acx * 1.7, 1.5, acz * 1.7)) * 8);
        const dungeonZ = acz * CHUNK_SIZE + 4 + Math.floor(Math.abs(this.dungeonNoise(acx * 1.3, 2.5, acz * 1.3)) * 8);
        const dungeonY = 10 + Math.floor(Math.abs(this.dungeonNoise(acx * 0.7, 3.5, acz * 0.7)) * 25);

        // Must be underground (y < 40)
        if (dungeonY >= 40) continue;

        const roomW = 7, roomH = 5, roomD = 7;

        // Skip if this chunk's Y range doesn't overlap the dungeon
        if (wy0 + CHUNK_SIZE <= dungeonY || wy0 > dungeonY + roomH) continue;

        // Check X/Z overlap (with some margin)
        const dxMin = dungeonX - 3;
        const dxMax = dungeonX + 3;
        const dzMin = dungeonZ - 3;
        const dzMax = dungeonZ + 3;
        if (wx0 + CHUNK_SIZE <= dxMin || wx0 > dxMax) continue;
        if (wz0 + CHUNK_SIZE <= dzMin || wz0 > dzMax) continue;

        this._placeDungeon(chunk, dungeonX, dungeonY, dungeonZ, roomW, roomH, roomD);
      }
    }
  }

  _placeDungeon(chunk, dx, dy, dz, roomW, roomH, roomD) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    for (let x = 0; x < roomW; x++) {
      for (let y = 0; y < roomH; y++) {
        for (let z = 0; z < roomD; z++) {
          const wx = dx + x - 3;
          const wy = dy + y;
          const wz = dz + z - 3;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

          const isWall = x === 0 || x === roomW - 1 || z === 0 || z === roomD - 1;
          const isFloor = y === 0;
          const isCeiling = y === roomH - 1;

          if (isFloor) {
            // Cobblestone floor
            chunk.setBlock(lx, ly, lz, BlockId.COBBLESTONE);
          } else if (isCeiling) {
            // Mossy stone ceiling
            chunk.setBlock(lx, ly, lz, BlockId.MOSS_STONE);
          } else if (isWall) {
            // Mixed moss_stone and cobblestone walls
            const wallVal = Math.abs(this.detailNoise(wx * 0.7, wy * 0.7, wz * 0.7));
            chunk.setBlock(lx, ly, lz, wallVal < 0.5 ? BlockId.MOSS_STONE : BlockId.COBBLESTONE);
          } else {
            // Interior air
            chunk.setBlock(lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // Spawner indicator: moss_stone block in center of floor + glowstone above it
    this._setBlock(chunk, dx - wx0, dy + 1 - wy0, dz - wz0, BlockId.MOSS_STONE);
    this._setBlock(chunk, dx - wx0, dy + 3 - wy0, dz - wz0, BlockId.GLOWSTONE);

    // Torches on walls (4 torches, one per wall midpoint at y+2)
    this._setBlock(chunk, dx - 3 - wx0, dy + 2 - wy0, dz - wz0, BlockId.TORCH); // west wall
    this._setBlock(chunk, dx + 3 - wx0, dy + 2 - wy0, dz - wz0, BlockId.TORCH); // east wall
    this._setBlock(chunk, dx - wx0, dy + 2 - wy0, dz - 3 - wz0, BlockId.TORCH); // north wall
    this._setBlock(chunk, dx - wx0, dy + 2 - wy0, dz + 3 - wz0, BlockId.TORCH); // south wall

    // Chest in corner (deterministic corner choice)
    const cornerChoice = Math.abs(this.detailNoise(dx * 0.3, dy * 0.3, dz * 0.3));
    let chestOffX, chestOffZ;
    if (cornerChoice < 0.25) { chestOffX = -2; chestOffZ = -2; }
    else if (cornerChoice < 0.5) { chestOffX = 2; chestOffZ = -2; }
    else if (cornerChoice < 0.75) { chestOffX = -2; chestOffZ = 2; }
    else { chestOffX = 2; chestOffZ = 2; }

    this._setBlock(chunk, dx + chestOffX - wx0, dy + 1 - wy0, dz + chestOffZ - wz0, BlockId.CHEST);

    // Second chest (50% chance, opposite corner)
    const secondChest = Math.abs(this.detailNoise(dx * 0.9, dy * 0.9, dz * 0.9));
    if (secondChest > 0.5) {
      this._setBlock(chunk, dx - chestOffX - wx0, dy + 1 - wy0, dz - chestOffZ - wz0, BlockId.CHEST);
    }

    // Entrance opening on south side (2 wide x 3 tall)
    for (let ey = 1; ey <= 3; ey++) {
      for (let ex = -1; ex <= 0; ex++) {
        this._setBlock(chunk, dx + ex - wx0, dy + ey - wy0, dz + 3 - wz0, BlockId.AIR);
      }
    }
  }

  // =========================================================================
  // 2. ABANDONED MINE — Tunnels with support beams (y 15-45)
  //    3 wide, 3 high corridors for 15-30 blocks, fences + planks supports
  //    Probability: ~1 per 6 chunks underground
  // =========================================================================

  _generateAbandonedMines(chunk) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    // Mines can span multiple chunks. Check nearby cells.
    const searchRadius = 2;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        // ~17% chance (1 per 6 chunks)
        const chance = this.mineNoise(acx * 0.17, 0.5, acz * 0.17);
        if (chance < 0.83) continue;

        // Mine Y position (between 15-45)
        const mineY = 15 + Math.floor(Math.abs(this.mineNoise(acx * 0.5, 1.5, acz * 0.5)) * 30);
        if (mineY < 15 || mineY > 45) continue;

        const corridorH = 4; // 3 air blocks + 1 for beam

        // Skip if Y doesn't overlap
        if (wy0 + CHUNK_SIZE <= mineY || wy0 > mineY + corridorH) continue;

        // Mine origin in world coords
        const mineX = acx * CHUNK_SIZE + 8;
        const mineZ = acz * CHUNK_SIZE + 8;

        // Determine corridor direction and length from noise
        const dirNoise = this.mineNoise(acx * 2.3, 2.5, acz * 2.3);
        const goX = dirNoise > 0; // true = X direction, false = Z direction
        const length = 15 + Math.floor(Math.abs(this.mineNoise(acx * 1.1, 3.5, acz * 1.1)) * 16);

        this._placeMineCorridor(chunk, mineX, mineY, mineZ, goX, length);

        // Branch corridor (T-intersection, perpendicular)
        const branchChance = this.mineNoise(acx * 0.9, 4.5, acz * 0.9);
        if (branchChance > 0.2) {
          const branchLen = 10 + Math.floor(Math.abs(branchChance) * 12);
          const branchOffset = Math.floor(length * 0.4);
          let branchX, branchZ;
          if (goX) {
            branchX = mineX + branchOffset;
            branchZ = mineZ;
          } else {
            branchX = mineX;
            branchZ = mineZ + branchOffset;
          }
          this._placeMineCorridor(chunk, branchX, mineY, branchZ, !goX, branchLen);
        }
      }
    }
  }

  _placeMineCorridor(chunk, startX, startY, startZ, goX, length) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;
    const corridorH = 4;

    for (let i = 0; i < length; i++) {
      const cx = goX ? startX + i : startX;
      const cz = goX ? startZ : startZ + i;

      for (let dPerp = -1; dPerp <= 1; dPerp++) {
        for (let dy = 0; dy < corridorH; dy++) {
          const wx = goX ? cx : cx + dPerp;
          const wy = startY + dy;
          const wz = goX ? cz + dPerp : cz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

          // Support beams every 4 blocks
          if (i % 4 === 0) {
            // Side fence pillars (perpendicular edges, dy 1-2)
            if ((dPerp === -1 || dPerp === 1) && dy >= 1 && dy <= 2) {
              chunk.setBlock(lx, ly, lz, BlockId.FENCE);
              continue;
            }
            // Top cross beam (planks at dy 3)
            if (dy === 3) {
              chunk.setBlock(lx, ly, lz, BlockId.PLANKS);
              continue;
            }
          }

          // Random rubble on the floor (cobblestone)
          if (dy === 0) {
            const rubbleVal = Math.abs(this.detailNoise(wx * 0.5, wy * 0.5, wz * 0.5));
            if (rubbleVal > 0.82) {
              chunk.setBlock(lx, ly, lz, BlockId.COBBLESTONE);
              continue;
            }
          }

          // Carve air
          chunk.setBlock(lx, ly, lz, BlockId.AIR);
        }
      }
    }

    // Place a chest partway along the corridor
    const chestPos = Math.floor(length * 0.6);
    const chestWx = goX ? startX + chestPos : startX - 1;
    const chestWz = goX ? startZ - 1 : startZ + chestPos;
    const chestLx = chestWx - wx0;
    const chestLy = startY - wy0;
    const chestLz = chestWz - wz0;
    if (chestLx >= 0 && chestLx < CHUNK_SIZE && chestLy >= 0 && chestLy < CHUNK_SIZE && chestLz >= 0 && chestLz < CHUNK_SIZE) {
      chunk.setBlock(chestLx, chestLy, chestLz, BlockId.CHEST);
    }

    // Torch near chest (on the floor next to it)
    const torchLx = chestWx - wx0 + (goX ? 0 : 1);
    const torchLy = startY + 1 - wy0;
    const torchLz = chestWz - wz0 + (goX ? 1 : 0);
    if (torchLx >= 0 && torchLx < CHUNK_SIZE && torchLy >= 0 && torchLy < CHUNK_SIZE && torchLz >= 0 && torchLz < CHUNK_SIZE) {
      chunk.setBlock(torchLx, torchLy, torchLz, BlockId.TORCH);
    }
  }

  // =========================================================================
  // 3. DESERT TEMPLE — Pyramid on surface (desert biome only)
  //    21x21 sandstone base, layered pyramid, hidden chamber with chest + TNT
  //    Probability: ~1 per 10 desert chunks (rare)
  // =========================================================================

  _generateDesertTemples(chunk, getHeight, getBiome, BIOME_DESERT) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    // Temples are large (21x21), so check a wider area of anchor positions
    const searchRadius = 2;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        // ~10% chance (1 per 10 chunks)
        const chance = this.templeNoise(acx * 0.23, acz * 0.23);
        if (chance < 0.80) continue;

        // Temple center position
        const templeX = acx * CHUNK_SIZE + 8;
        const templeZ = acz * CHUNK_SIZE + 8;

        // Must be in desert biome
        const biome = getBiome(templeX, templeZ);
        if (biome !== BIOME_DESERT) continue;

        // Also check a few surrounding points to ensure mostly desert
        const b2 = getBiome(templeX + 8, templeZ);
        const b3 = getBiome(templeX - 8, templeZ);
        const b4 = getBiome(templeX, templeZ + 8);
        const b5 = getBiome(templeX, templeZ - 8);
        if (b2 !== BIOME_DESERT || b3 !== BIOME_DESERT || b4 !== BIOME_DESERT || b5 !== BIOME_DESERT) continue;

        const surfaceY = getHeight(templeX, templeZ);

        // The pyramid extends from surfaceY down to surfaceY-5 (hidden chamber)
        // and up to surfaceY + 11 (pyramid top)
        const pyramidBottom = surfaceY - 5;
        const pyramidTop = surfaceY + 11;

        // Skip if Y doesn't overlap
        if (wy0 + CHUNK_SIZE <= pyramidBottom || wy0 > pyramidTop) continue;

        // Check X/Z overlap
        const halfSize = 10; // 21/2
        if (wx0 + CHUNK_SIZE <= templeX - halfSize || wx0 > templeX + halfSize) continue;
        if (wz0 + CHUNK_SIZE <= templeZ - halfSize || wz0 > templeZ + halfSize) continue;

        this._placeDesertTemple(chunk, templeX, surfaceY, templeZ);
      }
    }
  }

  _placeDesertTemple(chunk, tx, surfaceY, tz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const halfBase = 10; // 21x21 base => -10 to +10

    // --- Pyramid layers (rising sandstone) ---
    // Layer 0 (ground level): 21x21 base
    // Each layer shrinks by 2 on each side (1 block step-in per layer)
    const pyramidHeight = 10; // layers 0-9 above surface
    for (let layer = 0; layer <= pyramidHeight; layer++) {
      const wy = surfaceY + layer;
      const halfLayer = halfBase - layer;
      if (halfLayer < 0) break;

      for (let dx = -halfLayer; dx <= halfLayer; dx++) {
        for (let dz = -halfLayer; dz <= halfLayer; dz++) {
          const wx = tx + dx;
          const wz = tz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

          // Edges only for upper layers (hollow pyramid), solid for base
          if (layer === 0) {
            // Solid base floor
            chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
          } else if (layer === pyramidHeight) {
            // Tip: small solid cap
            chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
          } else {
            // Walls: only edges get blocks, interior is air
            const isEdge = Math.abs(dx) === halfLayer || Math.abs(dz) === halfLayer;
            if (isEdge) {
              chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
            } else {
              chunk.setBlock(lx, ly, lz, BlockId.AIR);
            }
          }
        }
      }
    }

    // --- Entry on the south side (cut opening through pyramid wall) ---
    for (let ey = 1; ey <= 3; ey++) {
      for (let ex = -1; ex <= 1; ex++) {
        const wy = surfaceY + ey;
        const wx = tx + ex;
        const wz = tz + halfBase; // south edge
        const lx = wx - wx0;
        const ly = wy - wy0;
        const lz = wz - wz0;
        if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
          chunk.setBlock(lx, ly, lz, BlockId.AIR);
        }
      }
    }

    // --- Entry corridor leading inside ---
    for (let corridor = 1; corridor <= 4; corridor++) {
      for (let ex = -1; ex <= 1; ex++) {
        for (let ey = 1; ey <= 3; ey++) {
          const wx = tx + ex;
          const wy = surfaceY + ey;
          const wz = tz + halfBase - corridor;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;
          if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
            chunk.setBlock(lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // --- Decorative columns at entrance ---
    for (let dy = 1; dy <= 4; dy++) {
      this._setBlock(chunk, tx - 2 - wx0, surfaceY + dy - wy0, tz + halfBase - wz0, BlockId.SANDSTONE);
      this._setBlock(chunk, tx + 2 - wx0, surfaceY + dy - wy0, tz + halfBase - wz0, BlockId.SANDSTONE);
    }

    // --- Hidden underground chamber (5x5x4, below center) ---
    const chamberY = surfaceY - 5;
    const chamberHalfW = 2; // 5x5 chamber

    for (let dy = 0; dy < 4; dy++) {
      for (let dx = -chamberHalfW; dx <= chamberHalfW; dx++) {
        for (let dz = -chamberHalfW; dz <= chamberHalfW; dz++) {
          const wx = tx + dx;
          const wy = chamberY + dy;
          const wz = tz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          if (lx < 0 || lx >= CHUNK_SIZE || ly < 0 || ly >= CHUNK_SIZE || lz < 0 || lz >= CHUNK_SIZE) continue;

          const isEdge = Math.abs(dx) === chamberHalfW || Math.abs(dz) === chamberHalfW;
          const isBottom = dy === 0;
          const isTop = dy === 3;

          if (isBottom) {
            // TNT trap under the floor (hidden under sandstone)
            if (Math.abs(dx) <= 1 && Math.abs(dz) <= 1) {
              chunk.setBlock(lx, ly, lz, BlockId.TNT);
            } else {
              chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
            }
          } else if (isTop) {
            chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
          } else if (isEdge) {
            chunk.setBlock(lx, ly, lz, BlockId.SANDSTONE);
          } else {
            chunk.setBlock(lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // Sandstone floor over the TNT (one layer above TNT)
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        this._setBlock(chunk, tx + dx - wx0, chamberY + 1 - wy0, tz + dz - wz0, BlockId.SANDSTONE);
      }
    }

    // Chest in the hidden chamber
    this._setBlock(chunk, tx - wx0, chamberY + 2 - wy0, tz - wz0, BlockId.CHEST);

    // Torch in chamber
    this._setBlock(chunk, tx + 1 - wx0, chamberY + 2 - wy0, tz + 1 - wz0, BlockId.TORCH);

    // --- Shaft from center of base floor down to chamber ---
    for (let sy = chamberY + 3; sy < surfaceY; sy++) {
      this._setBlock(chunk, tx - wx0, sy - wy0, tz - wz0, BlockId.AIR);
      this._setBlock(chunk, tx + 1 - wx0, sy - wy0, tz - wz0, BlockId.AIR);
    }
  }

  // =========================================================================
  // 4. RUINED PORTAL — Partial obsidian frame on surface (any biome)
  //    Incomplete nether portal frame, netherrack scattered, chest nearby
  //    Probability: ~1 per 12 chunks
  // =========================================================================

  _generateRuinedPortals(chunk, getHeight, getBiome) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    // Check nearby chunk positions for portals
    const searchRadius = 1;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        // ~8% chance (1 per 12 chunks)
        const chance = this.portalNoise(acx * 0.19, acz * 0.19);
        if (chance < 0.84) continue;

        // Portal position (world coords)
        const portalX = acx * CHUNK_SIZE + 4 + Math.floor(Math.abs(this.portalNoise(acx * 1.3 + 50, acz * 1.3 + 50)) * 8);
        const portalZ = acz * CHUNK_SIZE + 4 + Math.floor(Math.abs(this.portalNoise(acx * 0.9 + 100, acz * 0.9 + 100)) * 8);
        const surfaceY = getHeight(portalX, portalZ);

        // Don't place underwater
        if (surfaceY <= 33) continue;

        // Portal frame is ~5 wide, 6 tall
        const portalTop = surfaceY + 6;

        // Skip if Y doesn't overlap
        if (wy0 + CHUNK_SIZE <= surfaceY || wy0 > portalTop) continue;

        // Check X/Z overlap
        if (wx0 + CHUNK_SIZE <= portalX - 3 || wx0 > portalX + 5) continue;
        if (wz0 + CHUNK_SIZE <= portalZ - 3 || wz0 > portalZ + 3) continue;

        this._placeRuinedPortal(chunk, portalX, surfaceY, portalZ);
      }
    }
  }

  _placeRuinedPortal(chunk, px, surfaceY, pz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    // Standard Nether portal frame shape (4 wide, 5 tall) oriented along X axis
    // Frame positions (relative to px, surfaceY+1, pz):
    //   Bottom: (0,0), (1,0), (2,0), (3,0)
    //   Left:   (0,1), (0,2), (0,3)
    //   Right:  (3,1), (3,2), (3,3)
    //   Top:    (0,4), (1,4), (2,4), (3,4)
    const frameBlocks = [
      // Bottom row
      { dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 2, dy: 0 }, { dx: 3, dy: 0 },
      // Left pillar
      { dx: 0, dy: 1 }, { dx: 0, dy: 2 }, { dx: 0, dy: 3 },
      // Right pillar
      { dx: 3, dy: 1 }, { dx: 3, dy: 2 }, { dx: 3, dy: 3 },
      // Top row
      { dx: 0, dy: 4 }, { dx: 1, dy: 4 }, { dx: 2, dy: 4 }, { dx: 3, dy: 4 },
    ];

    // Deterministically decide which 2-4 blocks to remove (making it "ruined")
    const removeCount = 2 + Math.floor(Math.abs(this.detailNoise(px * 0.4, surfaceY * 0.4, pz * 0.4)) * 3);
    const removedIndices = new Set();

    // Use noise to pick which blocks to remove (prefer top row for visual interest)
    for (let r = 0; r < removeCount; r++) {
      const noiseVal = Math.abs(this.detailNoise(px * (0.7 + r * 0.3), (surfaceY + r) * 0.5, pz * (0.7 + r * 0.2)));
      const idx = Math.floor(noiseVal * frameBlocks.length);
      removedIndices.add(idx % frameBlocks.length);
    }

    // Place the frame (minus removed blocks)
    for (let i = 0; i < frameBlocks.length; i++) {
      if (removedIndices.has(i)) continue;

      const fb = frameBlocks[i];
      const wx = px + fb.dx;
      const wy = surfaceY + 1 + fb.dy;
      const lx = wx - wx0;
      const ly = wy - wy0;
      const lz = pz - wz0;

      if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
        chunk.setBlock(lx, ly, lz, BlockId.OBSIDIAN);
      }
    }

    // Clear interior of portal frame
    for (let dy = 1; dy <= 3; dy++) {
      for (let dx = 1; dx <= 2; dx++) {
        const wx = px + dx;
        const wy = surfaceY + 1 + dy;
        const lx = wx - wx0;
        const ly = wy - wy0;
        const lz = pz - wz0;
        if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
          chunk.setBlock(lx, ly, lz, BlockId.AIR);
        }
      }
    }

    // Netherrack scattered around base (3-block radius)
    for (let dx = -2; dx <= 5; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        const wx = px + dx;
        const wz = pz + dz;
        const nVal = Math.abs(this.detailNoise(wx * 0.6, surfaceY * 0.6, wz * 0.6));

        if (nVal > 0.55) {
          // Place netherrack on the surface
          const lx = wx - wx0;
          const ly = surfaceY - wy0;
          const lz = wz - wz0;
          if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
            chunk.setBlock(lx, ly, lz, BlockId.NETHERRACK);
          }
          // Some netherrack one block up too
          if (nVal > 0.8) {
            const ly2 = surfaceY + 1 - wy0;
            if (lx >= 0 && lx < CHUNK_SIZE && ly2 >= 0 && ly2 < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
              chunk.setBlock(lx, ly2, lz, BlockId.NETHERRACK);
            }
          }
        }
      }
    }

    // Chest nearby (offset to the side)
    const chestX = px + 4;
    const chestZ = pz + 1;
    this._setBlock(chunk, chestX - wx0, surfaceY + 1 - wy0, chestZ - wz0, BlockId.CHEST);

    // Glowstone block next to portal for atmosphere
    this._setBlock(chunk, px - 1 - wx0, surfaceY + 1 - wy0, pz - wz0, BlockId.GLOWSTONE);
  }

  // =========================================================================
  // =========================================================================
  // 5. STRONGHOLD — Underground fortress with End Portal room
  //    One per world at deterministic position. ~30x10x30 area.
  // =========================================================================

  _generateStronghold(chunk) {
    if (!this._strongholdPos) return;

    const sx = this._strongholdPos.x;
    const sy = this._strongholdPos.y;
    const sz = this._strongholdPos.z;

    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    // Check if this chunk overlaps the stronghold bounding box (30x12x30)
    const halfW = 15;
    const height = 12;
    if (wx0 + CHUNK_SIZE <= sx - halfW || wx0 >= sx + halfW) return;
    if (wy0 + CHUNK_SIZE <= sy || wy0 >= sy + height) return;
    if (wz0 + CHUNK_SIZE <= sz - halfW || wz0 >= sz + halfW) return;

    // --- Main room (stone bricks shell) ---
    for (let dx = -halfW; dx < halfW; dx++) {
      for (let dz = -halfW; dz < halfW; dz++) {
        for (let dy = 0; dy < height; dy++) {
          const wx = sx + dx;
          const wy = sy + dy;
          const wz = sz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          // Walls, floor, ceiling
          const isWall = dx === -halfW || dx === halfW - 1 || dz === -halfW || dz === halfW - 1;
          const isFloor = dy === 0;
          const isCeiling = dy === height - 1;

          if (isFloor || isCeiling) {
            this._setBlock(chunk, lx, ly, lz, BlockId.END_STONE_BRICKS);
          } else if (isWall) {
            // Pillars at corners and every 5 blocks
            if (dx % 5 === 0 || dz % 5 === 0) {
              this._setBlock(chunk, lx, ly, lz, BlockId.STONE);
            } else {
              this._setBlock(chunk, lx, ly, lz, BlockId.END_STONE_BRICKS);
            }
          } else {
            // Interior: clear to air
            this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // --- End Portal room (center, 7x7 area) ---
    const portalY = sy + 1; // portal sits on floor level + 1
    // Place END_PORTAL_FRAME ring (5x5 without corners)
    const frameOffsets = [
      { x: -1, z: -2 }, { x: 0, z: -2 }, { x: 1, z: -2 },
      { x: -1, z: 2 },  { x: 0, z: 2 },  { x: 1, z: 2 },
      { x: -2, z: -1 }, { x: -2, z: 0 }, { x: -2, z: 1 },
      { x: 2, z: -1 },  { x: 2, z: 0 },  { x: 2, z: 1 },
    ];
    for (const off of frameOffsets) {
      this._setBlock(chunk, sx + off.x - wx0, portalY - wy0, sz + off.z - wz0, BlockId.END_PORTAL_FRAME);
    }

    // Lava pool under portal (Y = sy)
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        this._setBlock(chunk, sx + dx - wx0, sy - wy0, sz + dz - wz0, BlockId.LAVA);
      }
    }

    // --- Library rooms (east wing) ---
    const libX = sx + 8;
    const libZ = sz;
    for (let dx = -3; dx <= 3; dx++) {
      for (let dz = -3; dz <= 3; dz++) {
        for (let dy = 1; dy <= 4; dy++) {
          const wx = libX + dx;
          const wy = sy + dy;
          const wz = libZ + dz;
          // Bookshelves on walls
          if ((Math.abs(dx) === 3 || Math.abs(dz) === 3) && dy <= 3) {
            this._setBlock(chunk, wx - wx0, wy - wy0, wz - wz0, BlockId.BOOKSHELF);
          }
        }
      }
    }
    // Library chest
    this._setBlock(chunk, libX - wx0, sy + 1 - wy0, libZ - wx0 > 0 ? libZ - wz0 : libZ - wz0, BlockId.CHEST);

    // --- Staircase entrance (south corridor) ---
    for (let step = 0; step < 8; step++) {
      const stairZ = sz + halfW - 1 - step;
      const stairY = sy + Math.min(step, 5);
      for (let dx = -1; dx <= 1; dx++) {
        // Clear air above
        for (let dy = 0; dy < 3; dy++) {
          this._setBlock(chunk, sx + dx - wx0, stairY + dy - wy0, stairZ - wz0, BlockId.AIR);
        }
        // Step block
        this._setBlock(chunk, sx + dx - wx0, stairY - wy0, stairZ - wz0, BlockId.END_STONE_BRICKS);
      }
    }

    // --- Torches for lighting ---
    const torchPositions = [
      { x: sx - 5, z: sz - 5 }, { x: sx + 5, z: sz - 5 },
      { x: sx - 5, z: sz + 5 }, { x: sx + 5, z: sz + 5 },
      { x: sx, z: sz - 4 }, { x: sx, z: sz + 4 },
    ];
    for (const t of torchPositions) {
      this._setBlock(chunk, t.x - wx0, sy + 2 - wy0, t.z - wz0, BlockId.TORCH);
    }
  }

  // =========================================================================
  // 6. JUNGLE TEMPLE — Cobblestone/moss_stone structure in jungle biome
  //    12x10x12, two floors, ladder, chest, TNT trap
  //    Probability: ~8% of jungle chunks
  // =========================================================================

  _generateJungleTemples(chunk, getHeight, getBiome, BIOME_JUNGLE) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    const searchRadius = 2;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        const chance = this.jungleTempleNoise(acx * 0.23, acz * 0.23);
        if (chance < 0.65) continue;

        const templeX = acx * CHUNK_SIZE + 8;
        const templeZ = acz * CHUNK_SIZE + 8;

        const biome = getBiome(templeX, templeZ);
        if (biome !== BIOME_JUNGLE) continue;

        const surfaceY = getHeight(templeX, templeZ);
        const structW = 12, structH = 10, structD = 12;
        const halfW = Math.floor(structW / 2);
        const halfD = Math.floor(structD / 2);

        if (wy0 + CHUNK_SIZE <= surfaceY || wy0 > surfaceY + structH) continue;
        if (wx0 + CHUNK_SIZE <= templeX - halfW || wx0 > templeX + halfW) continue;
        if (wz0 + CHUNK_SIZE <= templeZ - halfD || wz0 > templeZ + halfD) continue;

        this._placeJungleTemple(chunk, templeX, surfaceY, templeZ);
      }
    }
  }

  _placeJungleTemple(chunk, tx, surfaceY, tz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const halfW = 6; // 12/2
    const halfD = 6;
    const wallH = 3;
    const totalH = 10; // two floors: 0-4 lower, 5-9 upper

    // --- Ground floor walls and floor ---
    for (let dx = -halfW; dx <= halfW; dx++) {
      for (let dz = -halfD; dz <= halfD; dz++) {
        for (let dy = 0; dy < totalH; dy++) {
          const wx = tx + dx;
          const wy = surfaceY + dy;
          const wz = tz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          const isWallX = Math.abs(dx) === halfW;
          const isWallZ = Math.abs(dz) === halfD;
          const isWall = isWallX || isWallZ;
          const isFloor = dy === 0;
          const isMidFloor = dy === 5; // second floor
          const isRoof = dy === totalH - 1;

          if (isFloor || isRoof) {
            this._setBlock(chunk, lx, ly, lz, BlockId.COBBLESTONE);
          } else if (isMidFloor) {
            // Mid-floor with hole for ladder
            if (dx === 0 && dz === 0) {
              this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
            } else {
              this._setBlock(chunk, lx, ly, lz, BlockId.COBBLESTONE);
            }
          } else if (isWall) {
            const wallVal = Math.abs(this.detailNoise(wx * 0.7, wy * 0.7, wz * 0.7));
            this._setBlock(chunk, lx, ly, lz, wallVal < 0.5 ? BlockId.MOSS_STONE : BlockId.COBBLESTONE);
          } else {
            this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // --- Ladder connecting floors (at center, dy 1-5) ---
    for (let dy = 1; dy <= 5; dy++) {
      this._setBlock(chunk, tx - wx0, surfaceY + dy - wy0, tz - wx0 > 0 ? tz - wz0 : tz - wz0, BlockId.LADDER);
    }

    // --- Torches inside (4 corners, lower floor) ---
    this._setBlock(chunk, tx - halfW + 1 - wx0, surfaceY + 2 - wy0, tz - halfD + 1 - wz0, BlockId.TORCH);
    this._setBlock(chunk, tx + halfW - 1 - wx0, surfaceY + 2 - wy0, tz - halfD + 1 - wz0, BlockId.TORCH);
    this._setBlock(chunk, tx - halfW + 1 - wx0, surfaceY + 2 - wy0, tz + halfD - 1 - wz0, BlockId.TORCH);
    this._setBlock(chunk, tx + halfW - 1 - wx0, surfaceY + 2 - wy0, tz + halfD - 1 - wz0, BlockId.TORCH);

    // --- Chest on upper floor ---
    this._setBlock(chunk, tx + 2 - wx0, surfaceY + 6 - wy0, tz + 2 - wz0, BlockId.CHEST);

    // --- TNT trap on lower floor (pressure plate at entry, TNT buried under floor) ---
    // Entry opening on south wall
    for (let ey = 1; ey <= 2; ey++) {
      this._setBlock(chunk, tx - wx0, surfaceY + ey - wy0, tz + halfD - wz0, BlockId.AIR);
      this._setBlock(chunk, tx + 1 - wx0, surfaceY + ey - wy0, tz + halfD - wz0, BlockId.AIR);
    }

    // Pressure plate just inside entry
    this._setBlock(chunk, tx - wx0, surfaceY + 1 - wy0, tz + halfD - 1 - wz0, BlockId.PRESSURE_PLATE);

    // TNT buried 1 block under the floor at entry
    this._setBlock(chunk, tx - wx0, surfaceY - 1 - wy0, tz + halfD - 1 - wz0, BlockId.TNT);
    this._setBlock(chunk, tx + 1 - wx0, surfaceY - 1 - wy0, tz + halfD - 1 - wz0, BlockId.TNT);
    this._setBlock(chunk, tx - 1 - wx0, surfaceY - 1 - wy0, tz + halfD - 1 - wz0, BlockId.TNT);
  }

  // =========================================================================
  // 7. IGLOO — Snow hemisphere in tundra biome
  //    7x5x7 dome, bed + crafting table inside, optional basement
  //    Probability: ~12% of tundra chunks
  // =========================================================================

  _generateIgloos(chunk, getHeight, getBiome, BIOME_TUNDRA) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    const searchRadius = 2;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        const chance = this.iglooNoise(acx * 0.23, acz * 0.23);
        if (chance < 0.60) continue;

        const iglooX = acx * CHUNK_SIZE + 8;
        const iglooZ = acz * CHUNK_SIZE + 8;

        const biome = getBiome(iglooX, iglooZ);
        if (biome !== BIOME_TUNDRA) continue;

        const surfaceY = getHeight(iglooX, iglooZ);
        const structH = 5;
        const halfR = 3; // radius 3 => 7x7

        if (wy0 + CHUNK_SIZE <= surfaceY || wy0 > surfaceY + structH + 3) continue;
        if (wx0 + CHUNK_SIZE <= iglooX - halfR || wx0 > iglooX + halfR) continue;
        if (wz0 + CHUNK_SIZE <= iglooZ - halfR || wz0 > iglooZ + halfR) continue;

        this._placeIgloo(chunk, iglooX, surfaceY, iglooZ);
      }
    }
  }

  _placeIgloo(chunk, ix, surfaceY, iz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const radius = 3;
    const radiusSq = radius * radius;

    // --- Build hemisphere shell ---
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dz = -radius; dz <= radius; dz++) {
        for (let dy = 0; dy <= radius; dy++) {
          const distSq = dx * dx + dy * dy + dz * dz;

          if (distSq > radiusSq + 1) continue; // outside dome

          const wx = ix + dx;
          const wy = surfaceY + dy;
          const wz = iz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          if (distSq >= radiusSq - 1) {
            // Shell
            this._setBlock(chunk, lx, ly, lz, BlockId.SNOW);
          } else if (dy > 0) {
            // Hollow interior (above floor)
            this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // Floor
    for (let dx = -radius + 1; dx <= radius - 1; dx++) {
      for (let dz = -radius + 1; dz <= radius - 1; dz++) {
        this._setBlock(chunk, ix + dx - wx0, surfaceY - wy0, iz + dz - wz0, BlockId.SNOW);
      }
    }

    // --- Entry opening (south side, 1 wide x 2 tall) ---
    this._setBlock(chunk, ix - wx0, surfaceY + 1 - wy0, iz + radius - wz0, BlockId.AIR);
    this._setBlock(chunk, ix - wx0, surfaceY + 2 - wy0, iz + radius - wz0, BlockId.AIR);

    // --- Interior furnishings ---
    this._setBlock(chunk, ix - 1 - wx0, surfaceY + 1 - wy0, iz - 1 - wz0, BlockId.BED);
    this._setBlock(chunk, ix + 1 - wx0, surfaceY + 1 - wy0, iz - 1 - wz0, BlockId.CRAFTING_TABLE);
    this._setBlock(chunk, ix - wx0, surfaceY + 2 - wy0, iz - 1 - wz0, BlockId.TORCH);

    // --- Optional basement (50% chance via detailNoise) ---
    const basementChance = Math.abs(this.detailNoise(ix * 0.3, surfaceY * 0.3, iz * 0.3));
    if (basementChance > 0.5) {
      // Trapdoor in floor
      this._setBlock(chunk, ix - wx0, surfaceY - wy0, iz - wz0, BlockId.TRAPDOOR);

      // 3x3x3 basement below
      const basementY = surfaceY - 4;
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          for (let dy = 0; dy < 3; dy++) {
            const wx = ix + dx;
            const wy = basementY + dy;
            const wz = iz + dz;
            const lx = wx - wx0;
            const ly = wy - wy0;
            const lz = wz - wz0;

            const isEdge = Math.abs(dx) === 1 || Math.abs(dz) === 1;
            const isBottom = dy === 0;
            const isTop = dy === 2;

            if (isBottom || isTop) {
              this._setBlock(chunk, lx, ly, lz, BlockId.SNOW);
            } else if (isEdge) {
              this._setBlock(chunk, lx, ly, lz, BlockId.SNOW);
            } else {
              this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
            }
          }
        }
      }

      // Ladder shaft from floor to basement
      for (let dy = basementY + 2; dy < surfaceY; dy++) {
        this._setBlock(chunk, ix - wx0, dy - wy0, iz - wz0, BlockId.LADDER);
      }

      // Chest in basement
      this._setBlock(chunk, ix - wx0, basementY + 1 - wy0, iz - 1 - wz0, BlockId.CHEST);
    }
  }

  // =========================================================================
  // 8. SHIPWRECK — Wooden hull partially submerged in water
  //    12x6x5 hull of planks/wood, tilted, 1-2 chests
  //    Probability: ~5% of water chunks
  // =========================================================================

  _generateShipwrecks(chunk, getHeight) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    const searchRadius = 3;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        const chance = this.shipwreckNoise(acx * 0.23, acz * 0.23);
        if (chance < 0.70) continue;

        const shipX = acx * CHUNK_SIZE + 8;
        const shipZ = acz * CHUNK_SIZE + 8;

        // Must be in water (surface below sea level)
        const surfaceH = getHeight(shipX, shipZ);
        if (surfaceH >= SEA_LEVEL) continue;

        // Ship sits on the ocean floor
        const shipY = surfaceH;
        const shipLen = 12, shipH = 6, shipW = 5;
        const halfW = Math.floor(shipW / 2);

        if (wy0 + CHUNK_SIZE <= shipY || wy0 > shipY + shipH) continue;
        if (wx0 + CHUNK_SIZE <= shipX - 1 || wx0 > shipX + shipLen) continue;
        if (wz0 + CHUNK_SIZE <= shipZ - halfW || wz0 > shipZ + halfW) continue;

        this._placeShipwreck(chunk, shipX, shipY, shipZ);
      }
    }
  }

  _placeShipwreck(chunk, sx, shipY, sz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const shipLen = 12;
    const halfW = 2; // 5 wide => -2 to +2

    // Hull shape: wider at center, narrower at bow/stern, tilted by 1 block offset per 4 length
    for (let i = 0; i < shipLen; i++) {
      // Tilt: offset Y based on position along length
      const tiltY = Math.floor(i / 4);
      // Width tapers at bow and stern
      const widthHere = i <= 1 || i >= shipLen - 2 ? 1 : halfW;

      for (let dz = -widthHere; dz <= widthHere; dz++) {
        for (let dy = 0; dy < 4; dy++) {
          const wx = sx + i;
          const wy = shipY + dy + tiltY;
          const wz = sz + dz;
          const lx = wx - wx0;
          const ly = wy - wy0;
          const lz = wz - wz0;

          const isBottom = dy === 0;
          const isSide = Math.abs(dz) === widthHere && dy > 0;

          if (isBottom) {
            // Hull bottom: planks
            this._setBlock(chunk, lx, ly, lz, BlockId.PLANKS);
          } else if (isSide && dy <= 2) {
            // Hull sides: wood logs
            this._setBlock(chunk, lx, ly, lz, BlockId.WOOD);
          } else if (!isSide && dy >= 1 && dy <= 2) {
            // Interior: air pocket
            this._setBlock(chunk, lx, ly, lz, BlockId.AIR);
          }
        }
      }
    }

    // --- Chests inside the hull ---
    // First chest at mid-ship
    const chest1X = sx + 5;
    this._setBlock(chunk, chest1X - wx0, shipY + 1 + Math.floor(5 / 4) - wy0, sz - wz0, BlockId.CHEST);

    // Second chest at stern (50% chance)
    const secondChance = Math.abs(this.detailNoise(sx * 0.4, shipY * 0.4, sz * 0.4));
    if (secondChance > 0.5) {
      const chest2X = sx + 9;
      this._setBlock(chunk, chest2X - wx0, shipY + 1 + Math.floor(9 / 4) - wy0, sz - wz0, BlockId.CHEST);
    }
  }

  // =========================================================================
  // 9. WITCH HUT — Stilted hut in swamp biome
  //    7x6x5, fence stilts, planks floor/walls, slab roof
  //    Probability: ~8% of swamp chunks
  // =========================================================================

  _generateWitchHuts(chunk, getHeight, getBiome, BIOME_SWAMP) {
    const cx = chunk.cx;
    const cy = chunk.cy;
    const cz = chunk.cz;
    const wx0 = cx * CHUNK_SIZE;
    const wy0 = cy * CHUNK_SIZE;
    const wz0 = cz * CHUNK_SIZE;

    const searchRadius = 2;
    for (let dcx = -searchRadius; dcx <= searchRadius; dcx++) {
      for (let dcz = -searchRadius; dcz <= searchRadius; dcz++) {
        const acx = cx + dcx;
        const acz = cz + dcz;

        const chance = this.witchHutNoise(acx * 0.23, acz * 0.23);
        if (chance < 0.65) continue;

        const hutX = acx * CHUNK_SIZE + 8;
        const hutZ = acz * CHUNK_SIZE + 8;

        const biome = getBiome(hutX, hutZ);
        if (biome !== BIOME_SWAMP) continue;

        const surfaceY = getHeight(hutX, hutZ);
        const stiltH = 3;
        const structH = 6; // stilts(3) + floor + walls(3) + roof
        const halfW = 3; // 7/2
        const halfD = 2; // 5/2

        if (wy0 + CHUNK_SIZE <= surfaceY || wy0 > surfaceY + structH) continue;
        if (wx0 + CHUNK_SIZE <= hutX - halfW || wx0 > hutX + halfW) continue;
        if (wz0 + CHUNK_SIZE <= hutZ - halfD || wz0 > hutZ + halfD) continue;

        this._placeWitchHut(chunk, hutX, surfaceY, hutZ);
      }
    }
  }

  _placeWitchHut(chunk, hx, surfaceY, hz) {
    const wx0 = chunk.cx * CHUNK_SIZE;
    const wy0 = chunk.cy * CHUNK_SIZE;
    const wz0 = chunk.cz * CHUNK_SIZE;

    const halfW = 3; // 7 wide
    const halfD = 2; // 5 deep
    const stiltH = 3;
    const floorY = surfaceY + stiltH;

    // --- 4 fence stilts at corners ---
    const stiltPositions = [
      { x: hx - halfW, z: hz - halfD },
      { x: hx + halfW, z: hz - halfD },
      { x: hx - halfW, z: hz + halfD },
      { x: hx + halfW, z: hz + halfD },
    ];
    for (const sp of stiltPositions) {
      for (let dy = 0; dy < stiltH; dy++) {
        this._setBlock(chunk, sp.x - wx0, surfaceY + dy - wy0, sp.z - wz0, BlockId.FENCE);
      }
    }

    // --- Floor (planks) ---
    for (let dx = -halfW; dx <= halfW; dx++) {
      for (let dz = -halfD; dz <= halfD; dz++) {
        this._setBlock(chunk, hx + dx - wx0, floorY - wy0, hz + dz - wz0, BlockId.PLANKS);
      }
    }

    // --- Walls (planks, 3 high) with entry gap ---
    for (let dy = 1; dy <= 3; dy++) {
      for (let dx = -halfW; dx <= halfW; dx++) {
        for (let dz = -halfD; dz <= halfD; dz++) {
          const isWallX = Math.abs(dx) === halfW;
          const isWallZ = Math.abs(dz) === halfD;
          if (!isWallX && !isWallZ) continue;

          // Entry gap: 1-block opening on south wall center, dy 1-2
          if (dz === halfD && dx === 0 && dy <= 2) continue;

          this._setBlock(chunk, hx + dx - wx0, floorY + dy - wy0, hz + dz - wz0, BlockId.PLANKS);
        }
      }
    }

    // --- Roof (wood slabs) ---
    for (let dx = -halfW; dx <= halfW; dx++) {
      for (let dz = -halfD; dz <= halfD; dz++) {
        this._setBlock(chunk, hx + dx - wx0, floorY + 4 - wy0, hz + dz - wz0, BlockId.SLAB_WOOD);
      }
    }

    // --- Interior furnishings ---
    this._setBlock(chunk, hx - 1 - wx0, floorY + 1 - wy0, hz - 1 - wz0, BlockId.CRAFTING_TABLE);
    this._setBlock(chunk, hx + 1 - wx0, floorY + 2 - wy0, hz - 1 - wz0, BlockId.TORCH);
  }

  // =========================================================================
  // Utility
  // =========================================================================

  /**
   * Set a block within chunk bounds (bounds-checked, no-op if outside)
   */
  _setBlock(chunk, lx, ly, lz, blockId) {
    if (lx >= 0 && lx < CHUNK_SIZE && ly >= 0 && ly < CHUNK_SIZE && lz >= 0 && lz < CHUNK_SIZE) {
      chunk.setBlock(lx, ly, lz, blockId);
    }
  }
}
